import { memo, useEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import {
  getDeviceTileStateCapabilityId,
  getIsDeviceQuickActionStateOnly,
} from '../../lib/deviceQuickAction.ts';
import { mergeRefs } from '../../lib/mergeRefs';
import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';
import { ContextMenu } from '../common/context-menu/ContextMenu';
import { dragTypes } from '../dnd/dragTypes';

import { useEventCallback } from '../../hooks/useEventCallback';
import { useQuickAction } from '../../hooks/useQuickAction.js';
import { useCapabilityValue } from '../../store/devices/useCapabilityValue.ts';
import { useDevice } from '../../store/devices/useDevices.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';

import { DeviceCapabilityList } from './DeviceCapabilityList';
import { DeviceContextMenuContent } from './DeviceContextMenuContent';
import { DeviceNameField } from './DeviceNameField';
import { DevicePremiumStatus } from './DevicePremiumStatus';
import {
  DeviceQuickAction,
  getDeviceQuickActionVariant,
  shouldRenderDeviceQuickAction,
} from './DeviceQuickAction';
import { DeviceTile } from './DeviceTile';
import { Indicator } from './Indicator';

export const MemoizedDevice = memo(function Device(props) {
  const { api } = useApi();
  const { device, isLoading, error } = useDevice({ deviceId: props.deviceId });

  const isLocked = device?.requiresPremium === true && api?.tier !== 'premium';
  const [name, setName] = useState(() => {
    const deviceName = device?.name.replace(/\s/g, ' ');
    return deviceName;
  });
  const [isRenaming, setIsRenaming] = useState(false);

  const {
    value,
    onQuickAction,
    quickActionId: rawQuickActionId,
    quickActionTitle,
  } = useQuickAction({
    device,
  });
  const isQuickActionOverrideNone = device?.ui?.quickActionOverride === '.none';
  const quickActionId = isQuickActionOverrideNone === true ? null : rawQuickActionId;
  const quickActionCapabilityObj =
    quickActionId != null && device?.capabilitiesObj != null
      ? (device.capabilitiesObj[quickActionId] ?? null)
      : null;
  const quickActionVariant = getDeviceQuickActionVariant({
    capabilityObj: quickActionCapabilityObj,
    device,
  });
  const isQuickActionStateOnly = getIsDeviceQuickActionStateOnly({
    capabilityObj: quickActionCapabilityObj,
    device,
  });
  const actionableQuickActionId = isQuickActionStateOnly === true ? null : quickActionId;
  const tileStateCapabilityId = getDeviceTileStateCapabilityId({
    device,
  });
  const [tileStateValue] = useCapabilityValue({
    deviceId: props.deviceId,
    capabilityId: tileStateCapabilityId,
  });
  const isDeviceTileOn =
    tileStateCapabilityId != null &&
    device.ready === true &&
    device.available === true &&
    isLocked !== true
      ? tileStateValue === true
      : null;
  const shouldRenderQuickAction = shouldRenderDeviceQuickAction({
    capabilityObj: quickActionCapabilityObj,
    device,
    isLocked,
    variant: quickActionVariant,
  });
  const quickActionBaseId = quickActionId?.split('.')[0] ?? null;
  const quickAction =
    shouldRenderQuickAction === true && quickActionBaseId != null && quickActionVariant != null ? (
      <DeviceQuickAction
        baseId={quickActionBaseId}
        capabilityObj={quickActionCapabilityObj}
        device={device}
        isActive={value === true}
        isTileOn={isDeviceTileOn}
        label={quickActionTitle}
        onPress={onQuickAction}
        variant={quickActionVariant}
      />
    ) : null;

  const capabilityListProps = {
    device: device,
    value: value,
    quickActionId: quickActionId,
    quickActionValue: value,
    onQuickAction: onQuickAction,
  };

  const hasCapabilityList = props.tileSize === DeviceTile.size.large;
  const capabilityList =
    hasCapabilityList === true ? <DeviceCapabilityList {...capabilityListProps} /> : null;

  const sharedProps = {
    name: name,
    deviceId: props.deviceId,
    device: device,
    iconObj: device?.iconObj,
    iconOverride: device?.iconOverride,
    capabilityList: capabilityList,
    indicator:
      isLocked === true ? (
        <DevicePremiumStatus />
      ) : (
        <Indicator device={device} tileSize={props.tileSize} />
      ),
    isOn: isDeviceTileOn,
    quickAction: quickAction,
    tileSize: props.tileSize,
  };

  // TODO check for isDragPreview/isRenaming for all fade animations
  function dragItemRender() {
    return (
      <DeviceTile
        {...sharedProps}
        capabilityList={
          props.tileSize === DeviceTile.size.large ? (
            <DeviceCapabilityList {...capabilityListProps} isDragPreview={true} />
          ) : null
        }
        isDragPreview={true}
      />
    );
  }

  const { dragRef, dropRef, isDragging } = useDeviceDragDrop({
    deviceId: props.deviceId,
    isDisabled: false,
    onDrop: props.onDrop,
    onRequestSwap: props.onRequestSwap,
    onDragEnd: props.onDragEnd,
    dragItemRender,
  });

  useEffect(() => {
    const deviceName = device?.name.replace(/\s/g, ' ');
    setName(deviceName);
  }, [device?.name]);

  if (device == null && isLoading === false && error == null) {
    return <DeviceTile {...sharedProps} isUnknown={true} />;
  }

  if (isLoading === true) {
    return <DeviceTile {...sharedProps} />;
  }

  if (device == null) {
    return null;
  }

  if (isRenaming) {
    async function renameDevice(event) {
      if (event.currentTarget.value.length === 0) {
        return;
      }
      const prevName = name;
      const nextName = event.currentTarget.value.replace(/\s/g, ' ');

      setName(nextName);
      setIsRenaming(false);

      try {
        await api.devices.updateDevice({
          id: device.id,
          device: {
            name: nextName,
          },
        });
      } catch (error) {
        setName(prevName);
        ToastManager.handleError(error);
      }
    }

    return (
      <DeviceTile {...sharedProps} isRenaming={true}>
        <DeviceNameField
          defaultValue={name}
          onBlur={(event) => {
            renameDevice(event).catch(console.error);
          }}
          onKeyDown={(event) => {
            switch (event.key) {
              case 'Enter':
                event.preventDefault();
                break;
              default:
                break;
            }
          }}
          onKeyUp={(event) => {
            switch (event.key) {
              case 'Enter':
                renameDevice(event).catch(console.error);

                break;
              case 'Escape':
                setIsRenaming(false);
                break;
              default:
                break;
            }
          }}
        />
      </DeviceTile>
    );
  }

  function handlePress(event) {
    if (event.ctrlKey && event.shiftKey) {
      console.log(device);
      return;
    }

    if (isLocked) {
      RouteManager.toPremiumRequiredDialog();
      return;
    }

    if (event.altKey) {
      if (isQuickActionOverrideNone === true || isQuickActionStateOnly === true) {
        return;
      }

      onQuickAction();
      return;
    }

    if (props.onControlsClick != null) {
      props.onControlsClick(event, device);
    }
  }

  function handleLongPress(event) {
    // onQuickAction();

    if (isLocked) {
      return;
    }
  }

  return (
    <ContextMenu
      content={
        <DeviceContextMenuContent
          deviceId={device.id}
          quickActionId={actionableQuickActionId}
          quickActionTitle={quickActionTitle}
          isLocked={isLocked}
          onQuickActionRequest={onQuickAction}
          onRenameRequest={() => {
            setIsRenaming(true);
          }}
        />
      }
    >
      {({ isOpen, onContextMenu }) => {
        return (
          <DeviceTile
            {...sharedProps}
            key={device.id}
            ref={mergeRefs([dragRef, dropRef])}
            value={value}
            isDragging={isDragging}
            isSelected={props.isSelected}
            isLocked={isLocked}
            onPress={handlePress}
            onLongPress={handleLongPress}
            onContextMenu={onContextMenu}
          />
        );
      }}
    </ContextMenu>
  );
});

function useDeviceDragDrop({
  deviceId,
  isDisabled,
  onRequestSwap,
  onDrop,
  onDragEnd,
  dragItemRender,
}) {
  const renderFunction = useEventCallback((...args) => {
    return dragItemRender(...args);
  });
  const handleDragEnd = useEventCallback((dragMonitor) => {
    onDragEnd?.({ didDrop: dragMonitor.didDrop() });
  });

  const [{ isDragging }, dragRef, preview] = useDrag(() => {
    return {
      type: dragTypes.DEVICE,
      item: () => {
        return {
          type: dragTypes.DEVICE,
          id: deviceId,
          render: renderFunction,
        };
      },
      canDrag(monitor) {
        return isDisabled !== true;
      },
      end(dragItem, dragMonitor) {
        handleDragEnd(dragMonitor);
      },
      collect(dragMonitor) {
        return {
          isDragging: dragMonitor.isDragging(),
        };
      },
    };
  }, [deviceId, isDisabled]);

  const [, dropRef] = useDrop(() => {
    return {
      accept: [dragTypes.DEVICE],
      hover(item) {
        if (item.id !== deviceId) {
          onRequestSwap?.({ firstId: deviceId, secondId: item.id });
        }
      },
      canDrop(item, monitor) {
        return isDisabled !== true;
      },
      drop(item, monitor) {
        onDrop?.(item);
      },
    };
  }, [deviceId, isDisabled, onRequestSwap, onDrop]);

  useEffect(() => {
    preview(getEmptyImage());
  }, [preview]);

  return {
    dragRef,
    dropRef,
    isDragging,
  };
}
